import { useEffect, useMemo, useState } from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ArrowUpDown,
    Filter,
    Search,
    CalendarClock,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Eye,
    Pencil,
    Home,
    History,
    School,
    ShoppingBag,
    CalendarIcon,
    Loader2,
    Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/formatters";
import { ScheduledOrder } from "@/lib/api";
import { useScheduledOrderManagement } from "@/hooks/useScheduledOrderManagement";
import { ErrorState } from "@/components/shared/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipWrapper } from "@/components/shared/TooltipWrapper";
import { ScheduledOrderDetailsModal } from "@/components/OrderInterface/Scheduled-Order/ScheduledOrderDetailsModal";
import { useUser } from "@/contexts/UserContext";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useScheduledOrderCreate } from "@/hooks/useScheduledOrderCreate";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import StateBadge from "@/components/ui/stateBadge";
import { CsatRatingModal } from "@/components/OrderInterface/CsatRatingModal";
import { ScheduledOrderCycleSelector } from "@/components/ScheduledOrders/ScheduledOrderCycleSelector";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";
import { isScheduledOrderClientViewEnabled, isScheduledOrderEvaluationsHiddenFromClient } from "@/features/scheduledOrderClientViewFeature";
import { isOpenCycleOnlyScheduledOrdersEnabled } from "@/features/openCycleOnlyScheduledOrdersFeature";
import { ScheduledOrderCycleFilter } from "@/components/ScheduledOrders/ScheduledOrderCycleFilter";

const ScheduledOrderSkeleton = () => (
    <main className="container mx-auto px-4 py-6 space-y-6" role="main" aria-busy="true" aria-label="Carregando seus pedidos programados">
        <div className="bg-white py-10 text-center mb-8 rounded-lg">
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-4 w-96 mx-auto mt-2" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-6 w-6" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-8 w-16" />
                    </CardContent>
                </Card>
            ))}
        </div>
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                </div>
                <div className="mt-4 rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableHead key={i}>
                                        <Skeleton className="h-5 w-24" />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    {Array.from({ length: 5 }).map((_, j) => (
                                        <TableCell key={j}>
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    </main>
);

export default function MyScheduledOrder() {
    const navigate = useNavigate();
    const { selectedClient } = useUser();
    const { createScheduledOrder } = useScheduledOrderCreate();
    const { confirm, ConfirmDialog } = useConfirmDialog();

    const [isCsatModalOpen, setIsCsatModalOpen] = useState(false);
    const [csatOrderNumber, setCsatOrderNumber] = useState("");

    const {
        tableData,
        totalRows,
        totalPages,
        isLoading,
        error,
        filters,
        setFilters,
        sorting,
        setSorting,
        pagination,
        setPagination,
        handleClearFilters,
        allOrders,
        sendIndividualOrderClient,
        isProcessingAction,
        refreshData,
        availableCycleYears,
        activeCycleYear,
        selectedCycleYear,
        setSelectedCycleYear,
    } = useScheduledOrderManagement(selectedClient?.codigo_cnx, "customer");

    const dataOrderClient = useMemo(() => allOrders.filter(o => o.clientCode === selectedClient?.codigo_cnx), [allOrders, selectedClient]);

    const [detailsModal, setDetailsModal] = useState<{
        isOpen: boolean;
        clientId: string | null;
        clientName: string | null;
        orderNumber: string | null;
        orderPayload: any | null;
        clientCode: string | null;
        cycleCode: string | number | null;
        scheduledOrderId: string | null;
    }>({
        isOpen: false,
        clientId: null,
        clientName: null,
        orderNumber: null,
        orderPayload: null,
        clientCode: null,
        cycleCode: null,
        scheduledOrderId: null,
    });

    const handleConfirmOrder = async (id: string, order: ScheduledOrder) => {
        const confirmed = await confirm({
            title: "Finalizar Pedido Programado",
            description: (
                <span className="flex flex-col gap-3">
                    <span>
                        Você está prestes a confirmar o pedido{" "}
                        <span className="font-bold text-foreground">{order.orderNumber}</span>.
                    </span>
                    <span className="text-sm border-l-2 border-amber-500 pl-3 py-1 bg-amber-50/50 text-amber-800">
                        Esta ação enviará os itens diretamente para o <strong>processamento</strong> e não poderá ser desfeita.
                    </span>
                    <span>Deseja confirmar o envio agora?</span>
                </span>
            ) as any,
            confirmLabel: "Sim, Confirmar!",
            cancelLabel: "Cancelar"
        });

        if (confirmed) {
            const success = await sendIndividualOrderClient(id, order);
            if (success) {
                setCsatOrderNumber(order.orderNumber || "");
                setIsCsatModalOpen(true);
            }
        }
    };

    const hasActiveFilters = useMemo(() => {
        return filters.searchQuery !== "" ||
            filters.status !== "all" ||
            filters.orderType !== "all" ||
            !!filters.deadlineRange?.from ||
            (isOpenCycleOnlyScheduledOrdersEnabled && !!activeCycleYear && selectedCycleYear !== activeCycleYear);
    }, [filters, activeCycleYear, selectedCycleYear]);

    const columns: ColumnDef<ScheduledOrder>[] = [
        {
            accessorKey: "orderNumber",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    aria-label="Número do Pedido: clique para ordenar"
                >
                    Núm. Pedido <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
            ),
            cell: ({ row }) => <div className="font-mono">{row.original.orderNumber}</div>
        },
        {
            accessorKey: "cycleCode",
            header: "Tipo",
            cell: ({ row }) => {
                const cycleCode = String(row.original.cycleCode || "");
                const shortCode = cycleCode.slice(-2);

                if (shortCode === '01' || !shortCode) {
                    return (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 flex w-fit items-center gap-1">Material</Badge>
                    );
                }

                if (shortCode === '02') {
                    return (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 flex w-fit items-center gap-1">2º Semestre</Badge>
                    );
                }

                if (shortCode === '00') {
                    return (
                        <div className="flex flex-col gap-0.5">
                            <Badge variant="secondary" className="flex w-fit items-center gap-1">Avaliações e Simulados</Badge>
                            <span className="text-[12px] text-muted-foreground font-medium pl-1">
                                {isScheduledOrderClientViewEnabled
                                    ? (row.original.shipmentBlock != null ? `Remessa ${row.original.shipmentBlock}` : null)
                                    : row.original.awaitingComposition
                                        ? "Aguardando composição"
                                        : `Remessa ${row.original.shipmentBlock ?? ""}`.trim()}
                            </span>
                        </div>
                    );
                }

                return <Badge variant="outline">{cycleCode}</Badge>;
            }
        },
        // A coluna "Cupom" saiu da lista de pedidos programados (20/10).
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const usesAuthoritativeFlow = isNewScheduledOrderCycleEnabledForCycle(Number(String(row.original.cycleCode || '').slice(0, 4)));
                // "Programado" é legado: simulados 2027+ seguem o fluxo do 2º semestre.
                const isSimulado = `${row.original.cycleCode}`.includes('00') && !usesAuthoritativeFlow;
                return <div className="flex flex-col items-start gap-1">
                    <StateBadge status={isSimulado ? 'order_queue' : row.original.status} />
                    {/* Badges de estado (Processado/Congelado/Acumulando) são só do backoffice; o cliente não vê. */}
                </div>
            },
        },
        {
            accessorKey: "createdAt",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    aria-label="Data de Criação: clique para ordenar"
                >
                    Criação <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
            ),
            cell: ({ row }) => formatDate(new Date(row.original.createdAt)),
        },
        {
            accessorKey: "deadline",
            header: ({ column }) => (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    aria-label="Data Limite: clique para ordenar"
                >
                    Data Limite <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
            ),
            cell: ({ row }) => row.original.deadline ? formatDate(new Date(`${row.original.deadline}T00:00:00`)) : "-",
        },
        {
            id: "actions",
            header: () => <div className="text-right sr-only">Ações</div>,
            cell: ({ row }) => {
                const isSimulado = (row.original.orderNumber || '').startsWith('PED-SIM');
                const isPending = row.original.status === 'pending_confirmation';
                const cutoffIsOpen = !row.original.cutoffAt
                    || new Date(row.original.cutoffAt).getTime() > Date.now();
                const isActionable = isPending
                    && !row.original.isFrozen && cutoffIsOpen;
                const isNewFlowCycle = isNewScheduledOrderCycleEnabledForCycle(
                    Number(String(row.original.cycleCode || '').slice(0, 4)));
                const canEdit = isActionable && (!isSimulado || isNewFlowCycle);
                const canConfirm = isActionable && (!isSimulado || isNewFlowCycle);

                return (
                    <div className="flex justify-end gap-1">
                        <TooltipWrapper content="Ver detalhes">
                            <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Ver detalhes do pedido ${row.original.orderNumber}`}
                                className="hover:bg-blue-200"
                                onClick={() => setDetailsModal({
                                    isOpen: true,
                                    clientCode: row.original.clientCode,
                                    clientId: row.original.clientId,
                                    clientName: row.original.clientName,
                                    orderNumber: row.original.orderNumber,
                                    orderPayload: row.original.orderPayload,
                                    cycleCode: row.original.cycleCode,
                                    scheduledOrderId: row.original.id
                                })}
                            >
                                <Eye className="h-4 w-4 text-blue-600" aria-hidden="true" />
                            </Button>
                        </TooltipWrapper>

                        {/* Quando for descomentar, precisa ajustar a lógica e as variáveis canEdit e canConfirm para programados */}
                        {import.meta.env.DEV && (canEdit || canConfirm) && (
                            <>
                                {canConfirm && (
                                    <TooltipWrapper content="Confirmar pedido" >
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            aria-label={`Confirmar pedido ${row.original.orderNumber}`}
                                            onClick={() => handleConfirmOrder(row.original.id, row.original)}
                                            disabled={isProcessingAction}
                                            className="hover:bg-green-200 hover:scale-110 transition-all duration-200"
                                        >
                                            {isProcessingAction ? (
                                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            ) : (
                                                <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
                                            )}
                                        </Button>
                                    </TooltipWrapper>
                                )}

                                {canEdit && (
                                    <TooltipWrapper content="Editar pedido">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            aria-label={`Editar pedido ${row.original.orderNumber}`}
                                            className="hover:bg-orange-200"
                                            onClick={() => navigate('/customer-portal/realizar-pedido', {
                                                state: {
                                                    typePedido: 'PROGRAMADO',
                                                    scheduledOrderId: row.original.id,
                                                    returnTo: '/customer-portal/pedido-programado',
                                                }
                                            })}
                                        >
                                            <Pencil className="h-4 w-4 text-orange-600" aria-hidden="true" />
                                        </Button>
                                    </TooltipWrapper>
                                )}
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    const table = useReactTable({
        // Confirmados e em processamento permanecem aqui até o processamento terminar
        // (status "completed"), quando passam a aparecer em Pedidos Realizados.
        // Visão do cliente: avaliações e simulados (ciclo terminado em 00) não aparecem (ver
        // scheduledOrderClientViewFeature; a API também deixa de devolvê-los ao perfil cliente).
        data: tableData.filter(e =>
            !(isScheduledOrderEvaluationsHiddenFromClient && `${e.cycleCode}`.endsWith('00'))
            && ((e.status == 'pending' && `${e.cycleCode}`.includes('00'))
                || e.status === 'pending_confirmation'
                || e.status === 'confirmed'
                || e.status === 'processing')),
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        manualPagination: true,
        pageCount: totalPages,
        state: {
            sorting,
            pagination,
        },
    });

    if (isLoading) return <ScheduledOrderSkeleton />;
    if (error) return <ErrorState message="Não foi possível carregar os pedidos programados." details={error.message} />;

    return (
        <main className="container mx-auto px-4 py-6" role="main" aria-labelledby="page-title">
            <div className="mb-6">
                <Breadcrumb aria-label="Navegação estrutural">
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/customer-portal" className="flex items-center gap-1">
                                    <Home className="h-3 w-3" aria-hidden="true" /> Início
                                </Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator aria-hidden="true" />
                        <BreadcrumbItem>
                            <BreadcrumbPage className="flex items-center gap-1">
                                <History className="h-3 w-3" aria-hidden="true" /> Pedidos Programados
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" id="page-title">
                        <CalendarClock className="h-6 w-6 text-blue-600" aria-hidden="true" />
                        Meus Pedidos Programados
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Acompanhe o status e os detalhes dos seus pedidos programados.
                    </p>
                </div>
                {selectedClient && (
                    <div className="bg-blue-50 border border-blue-100 px-4 py-2 rounded-lg flex items-center gap-3" role="status" aria-label="Unidade selecionada">
                        <div className="bg-blue-100 p-2 rounded-full">
                            <School className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider leading-none mb-1">Unidade Selecionada</p>
                            <p className="text-sm font-bold text-blue-900 leading-none">{selectedClient.clientFantasyName || selectedClient.nome_cliente}</p>
                        </div>
                    </div>
                )}
            </div>

            {!isOpenCycleOnlyScheduledOrdersEnabled && (
                <ScheduledOrderCycleSelector
                    years={availableCycleYears}
                    activeYear={activeCycleYear}
                    selectedYear={selectedCycleYear}
                    onSelect={setSelectedCycleYear}
                />
            )}

            <section aria-labelledby="filters-title">
                <Card className={cn("border-border shadow-lg", hasActiveFilters && "border-primary/20 bg-primary/5")}>
                    <CardHeader>
                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                            <Filter className="h-5 w-5" aria-hidden="true" /> Filtros Avançados
                            {hasActiveFilters && <Badge variant="secondary" className="ml-2">Ativos</Badge>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            {isOpenCycleOnlyScheduledOrdersEnabled && (
                                <ScheduledOrderCycleFilter
                                    years={availableCycleYears}
                                    activeYear={activeCycleYear}
                                    selectedYear={selectedCycleYear}
                                    onSelect={setSelectedCycleYear}
                                />
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="search-filter">Busca Rápida</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                    <Input
                                        id="search-filter"
                                        placeholder="ID, cliente..."
                                        value={filters.searchQuery}
                                        onChange={(e) => setFilters(f => ({ ...f, searchQuery: e.target.value }))}
                                        className="pl-10"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="status-filter">Status</Label>
                                <Select value={filters.status} onValueChange={(value) => setFilters(f => ({ ...f, status: value as any }))}>
                                    <SelectTrigger id="status-filter" aria-label="Status do pedido">
                                        <SelectValue placeholder="Todos os status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos os Status</SelectItem>
                                        <SelectItem value="order_queue">Programado</SelectItem>
                                        <SelectItem value="pending_confirmation">Aguardando confirmação</SelectItem>
                                        <SelectItem value="confirmed">Confirmado</SelectItem>
                                        <SelectItem value="cancelled">Cancelado</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="type-filter">Tipo do Pedido</Label>
                                <Select value={filters.orderType} onValueChange={(value) => setFilters(f => ({ ...f, orderType: value }))}>
                                    <SelectTrigger id="type-filter" aria-label="Tipo do pedido">
                                        <SelectValue placeholder="Todos os tipos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos os Tipos</SelectItem>
                                        <SelectItem value="programado">2º Semestre</SelectItem>
                                        {!isScheduledOrderEvaluationsHiddenFromClient && Array.from({ length: 8 }, (_, i) => (
                                            <SelectItem key={i + 1} value={`remessa-${i + 1}`}>
                                                Avaliações e Simulados - Remessa {i + 1}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Data Limite (Período)</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "w-full justify-start text-left font-normal",
                                                !filters.deadlineRange?.from && "text-muted-foreground"
                                            )}
                                            aria-label="Selecionar período de data limite"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                            {filters.deadlineRange?.from ? (
                                                filters.deadlineRange.to ? (
                                                    <>
                                                        {format(filters.deadlineRange.from, "dd/MM/yy", { locale: ptBR })} -{" "}
                                                        {format(filters.deadlineRange.to, "dd/MM/yy", { locale: ptBR })}
                                                    </>
                                                ) : (
                                                    format(filters.deadlineRange.from, "dd/MM/yy", { locale: ptBR })
                                                )
                                            ) : (
                                                <span>Filtrar por data</span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            initialFocus
                                            mode="range"
                                            defaultMonth={filters.deadlineRange?.from}
                                            selected={{
                                                from: filters.deadlineRange?.from,
                                                to: filters.deadlineRange?.to,
                                            }}
                                            onSelect={(range) =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    deadlineRange: {
                                                        from: range?.from,
                                                        to: range?.to,
                                                    },
                                                }))
                                            }
                                            numberOfMonths={2}
                                            locale={ptBR}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button
                                variant={!hasActiveFilters ? 'ghost' : 'outline'}
                                size="sm"
                                onClick={handleClearFilters}
                                disabled={!hasActiveFilters}
                                className="text-xs"
                            >
                                Limpar Filtros
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </section>

            <section aria-labelledby="table-title" className="mt-6">
                {/* Status Announcer for Screen Readers - WCAG 4.1.3 */}
                <div className="sr-only" aria-live="polite" role="status">
                    {hasActiveFilters
                        ? `${tableData.length} pedidos encontrados para os filtros aplicados.`
                        : `Mostrando todos os ${tableData.length} pedidos.`}
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle id="table-title">Listagem de Pedidos</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {selectedClient != null ? (
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <TableRow key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => {
                                                    const isSortable = header.column.getCanSort();
                                                    const sortDirection = header.column.getIsSorted();

                                                    return (
                                                        <TableHead
                                                            key={header.id}
                                                            scope="col"
                                                            aria-sort={
                                                                !isSortable
                                                                    ? undefined
                                                                    : sortDirection === "asc"
                                                                        ? "ascending"
                                                                        : sortDirection === "desc"
                                                                            ? "descending"
                                                                            : "none"
                                                            }
                                                        >
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </TableHead>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableHeader>
                                    <TableBody>
                                        {table.getRowModel().rows?.length ? (
                                            table.getRowModel().rows.map((row) => (
                                                <TableRow key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <TableCell key={cell.id}>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={columns.length}
                                                    className="h-24 text-center"
                                                >
                                                    <div className="py-12 text-center" role="status">
                                                        <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                                                        <h3 className="mt-4 text-lg font-medium">
                                                            Nenhum pedido encontrado
                                                        </h3>
                                                        <p className="mt-2 text-sm text-muted-foreground">
                                                            {hasActiveFilters
                                                                ? "Tente ajustar os filtros de busca."
                                                                : "Você ainda não possui pedidos realizados."}
                                                        </p>
                                                        {hasActiveFilters && tableData.length === 0 && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="mt-4"
                                                                onClick={handleClearFilters}
                                                                aria-label="Limpar filtros para ver todos os pedidos"
                                                            >
                                                                Limpar Filtros
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="py-12 text-center" role="alert">
                                <School className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                                <h3 className="mt-4 text-lg font-medium">
                                    Selecione uma unidade escolar.
                                </h3>
                            </div>
                        )}
                    </CardContent>
                    {totalPages > 1 && (
                        <nav className="flex items-center justify-between p-4 border-t" aria-label="Navegação de páginas">
                            <div className="flex-1 text-sm text-muted-foreground" aria-live="polite">
                                {totalRows} pedido(s) encontrado(s).
                            </div>
                            <div className="flex items-center space-x-6 lg:space-x-8">
                                <div className="flex w-[120px] items-center justify-center text-sm font-medium">
                                    Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => table.setPageIndex(0)}
                                        disabled={!table.getCanPreviousPage()}
                                        aria-label="Ir para a primeira página"
                                    >
                                        <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => table.previousPage()}
                                        disabled={!table.getCanPreviousPage()}
                                        aria-label="Ir para a página anterior"
                                    >
                                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => table.nextPage()}
                                        disabled={!table.getCanNextPage()}
                                        aria-label="Ir para a próxima página"
                                    >
                                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                        disabled={!table.getCanNextPage()}
                                        aria-label="Ir para a última página"
                                    >
                                        <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                                    </Button>
                                </div>
                            </div>
                        </nav>
                    )}
                </Card>
            </section>

            <ScheduledOrderDetailsModal
                isOpen={detailsModal.isOpen}
                onClose={() => setDetailsModal(prev => ({ ...prev, isOpen: false }))}
                clientId={detailsModal.clientId}
                clientCode={detailsModal.clientCode}
                clientName={detailsModal.clientName}
                orderNumber={detailsModal.orderNumber}
                orderPayload={detailsModal.orderPayload}
                cycleCode={detailsModal.cycleCode}
                scheduledOrderId={detailsModal.scheduledOrderId}
            />

            <CsatRatingModal
                isOpen={isCsatModalOpen}
                onClose={() => setIsCsatModalOpen(false)}
                orderNumber={csatOrderNumber}
            />

            <ConfirmDialog />
        </main >
    );
}
